import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { listingsAPI, dataAPI, uploadFileToS3, paymentsAPI } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  X,
  Check,
  ImagePlus,
  Truck,
  Loader2,
} from 'lucide-react';

const CONDITIONS = [
  { id: 'new', name: 'Nuevo' },
  { id: 'used', name: 'Usado' },
  { id: 'certified', name: 'Certificado' },
  { id: 'salvage', name: 'Para reparar' },
];

const YEARS = Array.from({ length: 35 }, (_, i) => 2025 - i);

export default function CreateListingPage() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get listing ID for edit mode
  const { user } = useAuth();
  const isEditMode = Boolean(id);
  
  const [step, setStep] = useState(1);
  const [makes, setMakes] = useState([]);
  const [states, setStates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingListing, setLoadingListing] = useState(isEditMode);
  const [customMake, setCustomMake] = useState(false); // toggles the "Otra…" free-text input
  
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    make: '',
    model: '',
    year: '',
    mileage: '',
    engine_hours: '',
    price: '',
    location_city: '',
    location_state: '',
    vin: '',
    condition: 'used',
    description: '',
    status: 'draft',
    images: [],
  });

  // Load existing listing data in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchListing = async () => {
        setLoadingListing(true);
        try {
          const response = await listingsAPI.getById(id);
          const listing = response.data;
          setFormData({
            category: listing.category || '',
            subcategory: listing.subcategory || '',
            make: listing.make || '',
            model: listing.model || '',
            year: listing.year?.toString() || '',
            mileage: listing.mileage?.toString() || '',
            engine_hours: listing.engine_hours?.toString() || '',
            price: listing.price?.toString() || '',
            location_city: listing.location_city || '',
            location_state: listing.location_state || '',
            vin: listing.vin || '',
            condition: listing.condition || 'used',
            description: listing.description || '',
            status: listing.status || 'draft',
            images: listing.images || [],
          });
        } catch (error) {
          console.error('Error loading listing:', error);
          toast.error('No se pudo cargar el anuncio');
          navigate('/dashboard');
        } finally {
          setLoadingListing(false);
        }
      };
      fetchListing();
    }
  }, [id, isEditMode, navigate]);

  // Fetch makes, states, and categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [makesRes, statesRes, catsRes] = await Promise.all([
          dataAPI.getMakes(formData.category || undefined),
          dataAPI.getStates(),
          api.get('/categories'),
        ]);
        setMakes(makesRes.data);
        // If the currently selected make is not in the fetched list (e.g. edit
        // mode loading a legacy free-text make), auto-switch to the "Otra…"
        // custom input so the user still sees their value.
        if (formData.make && !makesRes.data.includes(formData.make)) {
          setCustomMake(true);
        }
        setStates(statesRes.data);
        setCategories(catsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [formData.category]);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // File input ref
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Sube la imagen a Amazon S3 mediante presigned URL
  const uploadToS3 = async (file) => {
    try {
      const { public_url, key } = await uploadFileToS3(file, 'listings');
      return {
        image_url: public_url,
        public_id: key,
        display_order: 0
      };
    } catch (error) {
      console.error('S3 upload error:', error);
      throw error;
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Limit to 10 images total
    const remainingSlots = 10 - formData.images.length;
    const filesToUpload = files.slice(0, remainingSlots);

    if (files.length > remainingSlots) {
      toast.warning(`Solo puedes agregar ${remainingSlots} imagen(es) más (máx. 10)`);
    }

    setUploading(true);
    setUploadProgress(0);

    const uploadedImages = [];
    for (let i = 0; i < filesToUpload.length; i++) {
      try {
        const uploaded = await uploadToS3(filesToUpload[i]);
        uploaded.display_order = formData.images.length + uploadedImages.length;
        uploadedImages.push(uploaded);
        setUploadProgress(((i + 1) / filesToUpload.length) * 100);
      } catch (error) {
        toast.error(`No se pudo subir ${filesToUpload[i].name}`);
      }
    }

    if (uploadedImages.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));
      toast.success(`${uploadedImages.length} imagen(es) subidas correctamente`);
    }

    setUploading(false);
    setUploadProgress(0);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      // Create a fake event to reuse handleFileSelect
      const fakeEvent = { target: { files } };
      await handleFileSelect(fakeEvent);
    }
  };

  const handleImageRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.category || !formData.make || !formData.model || !formData.year) {
          toast.error('Completa todos los campos obligatorios');
          return false;
        }
        break;
      case 2:
        if (!formData.price || !formData.location_city || !formData.location_state) {
          toast.error('Completa todos los campos obligatorios');
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async (publishNow = false) => {
    setLoading(true);
    try {
      const listingData = {
        ...formData,
        year: parseInt(formData.year),
        mileage: formData.mileage ? parseInt(formData.mileage) : null,
        engine_hours: formData.engine_hours ? parseInt(formData.engine_hours) : null,
        price: parseFloat(formData.price),
        status: publishNow ? 'active' : (isEditMode ? formData.status : 'draft'),
      };

      let response;
      if (isEditMode) {
        response = await listingsAPI.update(id, listingData);
        toast.success('¡Anuncio actualizado correctamente!');
        navigate(`/listings/${response.data.id}`);
        return;
      }

      response = await listingsAPI.create(listingData);
      const created = response.data;

      // If the user wanted to publish but the backend forced status=draft + paid=false,
      // it means they're over the free quota. Start the Stripe Checkout flow.
      if (publishNow && created.status === 'draft' && created.paid === false) {
        toast.info('Has superado tu cuota gratuita. Te redirigimos al pago para publicar.');
        try {
          const checkout = await paymentsAPI.createListingCheckout(created.id, window.location.origin);
          window.location.href = checkout.data.url;
          return;
        } catch (err) {
          toast.error(getErrorMessage(err, 'No se pudo iniciar el pago'));
          navigate(`/dashboard`);
          return;
        }
      }

      toast.success(publishNow ? '¡Anuncio publicado!' : 'Anuncio guardado como borrador');
      navigate(`/listings/${created.id}`);
    } catch (error) {
      toast.error(getErrorMessage(error, `No se pudo ${isEditMode ? 'actualizar' : 'crear'} el anuncio`));
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Información del vehículo' },
    { number: 2, title: 'Detalles y precio' },
    { number: 3, title: 'Fotos' },
    { number: 4, title: 'Revisión' },
  ];

  // Show loading skeleton while fetching existing listing
  if (loadingListing) {
    return (
      <div className="min-h-screen bg-secondary p-8">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="bg-white border border-border rounded-md p-6">
            <Skeleton className="h-8 w-48 mb-6" />
            <div className="grid md:grid-cols-2 gap-6">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary" data-testid={isEditMode ? "edit-listing-page" : "create-listing-page"}>
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-muted-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />Atrás</Button>
          </div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
            {isEditMode ? 'Editar anuncio' : 'Crear nuevo anuncio'}
          </h1>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-6">
            {steps.map((s, i) => (
              <React.Fragment key={s.number}>
                <div className={`flex items-center gap-2 ${step >= s.number ? 'text-accent' : 'text-muted-foreground'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step > s.number ? 'bg-accent text-white' : 
                    step === s.number ? 'bg-accent text-white' : 
                    'bg-muted text-muted-foreground'
                  }`}>
                    {step > s.number ? <Check className="h-4 w-4" /> : s.number}
                  </div>
                  <span className="hidden sm:block text-sm font-medium">{s.title}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-1 ${step > s.number ? 'bg-accent' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="bg-white border border-border rounded-md p-6 md:p-8">
          {/* Step 1: Vehicle Info */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-6">Información del vehículo</h2>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Category */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">
                    Categoría *
                  </Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleChange('category', value)}
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Selecciona una categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Year */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">
                    Año *
                  </Label>
                  <Select
                    value={formData.year}
                    onValueChange={(value) => handleChange('year', value)}
                  >
                    <SelectTrigger data-testid="year-select">
                      <SelectValue placeholder="Selecciona un año" />
                    </SelectTrigger>
                    <SelectContent>
                      {YEARS.map((year) => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Make */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">
                    Marca *
                  </Label>
                  <Select
                    value={customMake ? '__other__' : formData.make}
                    onValueChange={(value) => {
                      if (value === '__other__') {
                        setCustomMake(true);
                        handleChange('make', '');
                      } else {
                        setCustomMake(false);
                        handleChange('make', value);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="make-select">
                      <SelectValue placeholder="Selecciona una marca" />
                    </SelectTrigger>
                    <SelectContent>
                      {makes.map((make) => (
                        <SelectItem key={make} value={make}>{make}</SelectItem>
                      ))}
                      <SelectItem key="__other__" value="__other__">Otra…</SelectItem>
                    </SelectContent>
                  </Select>
                  {customMake && (
                    <Input
                      value={formData.make}
                      onChange={(e) => handleChange('make', e.target.value)}
                      placeholder="Escribe el nombre de la marca"
                      className="mt-2"
                      data-testid="make-custom-input"
                    />
                  )}
                </div>

                {/* Model */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">
                    Modelo *
                  </Label>
                  <Input
                    value={formData.model}
                    onChange={(e) => handleChange('model', e.target.value)}
                    placeholder="Ingresa el modelo"
                    className="h-12"
                    data-testid="model-input"
                  />
                </div>

                {/* Condition */}
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">
                    Condición *
                  </Label>
                  <RadioGroup
                    value={formData.condition}
                    onValueChange={(value) => handleChange('condition', value)}
                    className="flex flex-wrap gap-4"
                  >
                    {CONDITIONS.map((cond) => (
                      <div key={cond.id} className="flex items-center space-x-2">
                        <RadioGroupItem value={cond.id} id={cond.id} />
                        <Label htmlFor={cond.id} className="cursor-pointer">{cond.name}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Details & Price */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-6">
                Detalles y precio
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Price */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">
                    Precio (COP) *
                  </Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    placeholder="0"
                    className="h-12"
                    data-testid="price-input"
                  />
                </div>

                {/* Mileage */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">Kilometraje</Label>
                  <Input
                    type="number"
                    value={formData.mileage}
                    onChange={(e) => handleChange('mileage', e.target.value)}
                    placeholder="Ingresa el kilometraje"
                    className="h-12"
                    data-testid="mileage-input"
                  />
                </div>

                {/* Engine Hours */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">Horas de motor</Label>
                  <Input
                    type="number"
                    value={formData.engine_hours}
                    onChange={(e) => handleChange('engine_hours', e.target.value)}
                    placeholder="Ingresa las horas de motor"
                    className="h-12"
                    data-testid="engine-hours-input"
                  />
                </div>

                {/* VIN */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">
                    VIN
                  </Label>
                  <Input
                    value={formData.vin}
                    onChange={(e) => handleChange('vin', e.target.value)}
                    placeholder="Ingresa el VIN"
                    className="h-12"
                    data-testid="vin-input"
                  />
                </div>

                {/* City */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">
                    Ciudad *
                  </Label>
                  <Input
                    value={formData.location_city}
                    onChange={(e) => handleChange('location_city', e.target.value)}
                    placeholder="Ingresa la ciudad"
                    className="h-12"
                    data-testid="city-input"
                  />
                </div>

                {/* State */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">
                    Departamento *
                  </Label>
                  <Select
                    value={formData.location_state}
                    onValueChange={(value) => handleChange('location_state', value)}
                  >
                    <SelectTrigger data-testid="state-select">
                      <SelectValue placeholder="Selecciona un departamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Description */}
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">Descripción</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Describe tu vehículo..."
                    rows={5}
                    data-testid="description-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Photos */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-3">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-6">Fotos</h2>

              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                multiple
                className="hidden"
                data-testid="file-input"
              />

              {/* Upload Progress */}
              {uploading && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-accent" />
                    <span className="text-sm">Subiendo imágenes...</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}

              {/* Drop Zone */}
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${
                  uploading 
                    ? 'border-muted bg-muted/50 cursor-not-allowed' 
                    : 'border-muted hover:border-accent hover:bg-accent/5'
                }`}
                data-testid="upload-dropzone"
              >
                <Upload className={`h-12 w-12 mx-auto mb-4 ${uploading ? 'text-muted-foreground' : 'text-accent'}`} />
                <p className="font-semibold mb-1">
                  {uploading ? 'Subiendo...' : 'Haz clic para subir o arrastra y suelta'}
                </p>
                <p className="text-sm text-muted-foreground">
                  PNG, JPG o WEBP hasta 10MB cada una (máx. 10 fotos)
                </p>
              </div>

              {/* Uploaded Images Grid */}
              {formData.images.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-sm font-semibold uppercase tracking-wide">
                    Fotos subidas ({formData.images.length}/10)
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((img, index) => (
                      <div key={img.image_url ? `${img.image_url}-${index}` : index} className="relative aspect-square bg-secondary rounded-sm overflow-hidden group">
                        <img src={img.image_url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                        {index === 0 && (
                          <div className="absolute top-2 left-2 bg-accent text-white text-xs px-2 py-1 rounded-sm font-semibold">
                            PRINCIPAL
                          </div>
                        )}
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleImageRemove(index)}
                          disabled={uploading}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-sm text-muted-foreground">
                La primera foto será la imagen principal. Pasa el cursor sobre una foto y haz clic en la X para eliminarla.
              </p>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-6" data-testid="step-4">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-6">
                Revisa tu anuncio
              </h2>

              <div className="bg-secondary p-6 rounded-sm space-y-4">
                <div className="flex items-center gap-4">
                  {formData.images[0] ? (
                    <img src={formData.images[0].image_url} alt="Main" className="w-32 h-24 object-cover rounded-sm" />
                  ) : (
                    <div className="w-32 h-24 bg-muted rounded-sm flex items-center justify-center">
                      <Truck className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase">
                      {formData.year} {formData.make} {formData.model}
                    </h3>
                    <p className="text-muted-foreground">{formData.category} • {formData.condition}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Precio:</span>
                    <span className="ml-2 font-semibold">${parseFloat(formData.price || 0).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Ubicación:</span>
                    <span className="ml-2">{formData.location_city}, {formData.location_state}</span>
                  </div>
                  {formData.mileage && (
                    <div>
                      <span className="text-muted-foreground">Kilometraje:</span>
                      <span className="ml-2">{parseInt(formData.mileage).toLocaleString()} km</span>
                    </div>
                  )}
                  {formData.vin && (
                    <div>
                      <span className="text-muted-foreground">VIN:</span>
                      <span className="ml-2 font-mono">{formData.vin}</span>
                    </div>
                  )}
                </div>

                {formData.description && (
                  <div>
                    <span className="text-sm text-muted-foreground block mb-1">Descripción:</span>
                    <p className="text-sm">{formData.description}</p>
                  </div>
                )}

                <div>
                  <span className="text-sm text-muted-foreground">Fotos:</span>
                  <span className="ml-2">{formData.images.length} subidas</span>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 1}
              className="h-12"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />Atrás</Button>

            <div className="flex gap-3">
              {step === 4 ? (
                <>
                  {!isEditMode && (
                    <Button
                      variant="outline"
                      onClick={() => handleSubmit(false)}
                      disabled={loading}
                      className="h-12"
                      data-testid="save-draft-btn"
                    >Guardar borrador</Button>
                  )}
                  <Button
                    className="btn-accent h-12"
                    onClick={() => handleSubmit(isEditMode ? false : true)}
                    disabled={loading}
                    data-testid="publish-btn"
                  >
                    {loading ? (isEditMode ? 'Guardando...' : 'Publicando...') : (isEditMode ? 'Guardar cambios' : 'Publicar anuncio')}
                  </Button>
                </>
              ) : (
                <Button
                  className="btn-accent h-12"
                  onClick={handleNext}
                  data-testid="next-btn"
                >Siguiente<ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
